import App from '@/App.vue';
import mixpanelInit from '@/mixpanel.js';
import router from '@/router.js';
import '@/style.css';
import { initializeStores } from '@/threejs/objectManager';
import { setup as setupAriesAuth } from '@disguise/aries-auth-js';
import Aura, { ConfirmationService, DialogService, PrimeVueConfig as PrimeVue, ToastService } from '@disguise/cloud-ui';
import * as Sentry from '@sentry/vue';
import { createPinia } from 'pinia';
import Tooltip from 'primevue/tooltip';
import { createApp } from 'vue';
import VuePapaParse from 'vue-papa-parse';

mixpanelInit();

const pinia = createPinia();
const app = createApp(App);

Sentry.init({
  app,
  dsn: import.meta.env.VITE_APP_SENTRY_PUBLIC_DSN,
  integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// Make sure to use Pinia before initializing any stores
app.use(pinia);

// Initialize Aries Auth
if (!import.meta.env.VITE_API_BASE_URL) {
  throw new Error('VITE_API_BASE_URL must be configured');
}

const ariesDomain = import.meta.env.VITE_API_BASE_URL.replace('/api', '');
const hostname = new URL(ariesDomain).hostname;
setupAriesAuth({
  domain: ariesDomain,
  client: 'browser',
  storageType: 'cookie',
  cookieDomain: hostname,
});

// Initialize stores after Pinia is installed
initializeStores();

app.use(PrimeVue, {
  theme: {
    preset: {
      ...Aura,
      semantic: {
        ...Aura.semantic,
        primary: {
          50: '{fuchsia.50}',
          100: '{fuchsia.100}',
          200: '{fuchsia.200}',
          300: '{fuchsia.300}',
          400: '{fuchsia.400}',
          500: '{fuchsia.500}',
          600: '{fuchsia.600}',
          700: '{fuchsia.700}',
          800: '{fuchsia.800}',
          900: '{fuchsia.900}',
          950: '{fuchsia.950}',
        },
      },
    },
    options: {
      prefix: 'p',
      darkModeSelector: '.dark',
      cssLayer: {
        name: 'primevue',
        order: 'tailwind-base, primevue, tailwind-utilities',
      },
    },
  },
});

app.use(router);
app.use(DialogService);
app.use(ToastService);
app.use(ConfirmationService);
app.directive('tooltip', Tooltip);
app.use(VuePapaParse);

app.mount('#app');
